import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import dayjs from 'dayjs';
import { Value } from 'react-calendar/dist/cjs/shared/types';
import { DateClickArg } from '@fullcalendar/interaction';

import Text from 'components/common/Text';
import { ScheduleParam } from 'containers/ProductRegist/hooks/types';
import { OptionSheetType } from 'containers/ProductRegist/components/OptionSelectRegist/hooks/useCalenderHooks';
import { ScheduleState } from 'types/api';
import Row from 'components/common/Row';
import CustomCalendarBase from 'components/Calendar/CustomCalendarBase';
import { EventContentArg, EventSourceInput } from '@fullcalendar/core';

const ScheduleCardBox = styled.div<{
  $isSelected: boolean;
}>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 4px;

  padding: 4px 8px;
  width: 109px;
  border-radius: 6px;
  border: none;
  background: #f7f7f8;

  color: #414b56;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px; /* 142.857% */
  letter-spacing: -0.056px;

  &:hover {
    border: 1px solid #414b56 !important;
    background: #fff !important;
  }

  ${props =>
    props.$isSelected
      ? css`
          border: 1px solid #000;
          background: #1b1c1e;
          color: #fff;
        `
      : css``}
`;

const StatusButton = styled.div<{ $background: string }>`
  width: 8px;
  height: 8px;

  background: ${props => props.$background};
  border-radius: 50%;
`;

interface UpdateCalendarIProps {
  isMobile: boolean;
  calendarList?: ScheduleParam[];
  isClearCalendarData?: boolean;
  checkedDate?: any[];
  setCheckedDate: Dispatch<SetStateAction<any[]>>;
  onChange: (value: Value) => void;
  handleIsOpenUpdateOptionSheet?: (param: {
    isOpen: boolean;
    sheetType: OptionSheetType;
    scheduleItem?: ScheduleParam;
  }) => void;
  handleInitScheduleItem: () => void;
}

const UpdateCalendar: React.FC<UpdateCalendarIProps> = props => {
  const {
    isMobile,
    calendarList,
    isClearCalendarData,
    checkedDate,
    setCheckedDate,
    onChange,
    handleIsOpenUpdateOptionSheet,
    handleInitScheduleItem,
  } = props;

  const events: EventSourceInput = calendarList?.map(item => {
    return {
      title: `${dayjs(item.term.startedAt).format('HH시 mm분')}/${dayjs(
        item.term.startedAt + item.term.duration,
      ).format('HH시 mm분')}`,
      start: dayjs(item.term.startedAt).format('YYYY-MM-DD'),
      extendedProps: { id: item.id, item: item, status: item.status },
    };
  });

  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const [selectedItem, setSeletedItem] = useState<number>();

  const today = new Date();

  // 90일 후의 날짜
  const ninetyDaysLater = new Date();
  ninetyDaysLater.setDate(today.getDate() + 90);

  const handleDateClick = (info: DateClickArg) => {
    const date = dayjs(info.date).toDate();

    const isSelected = selectedDates.some(selectedDate =>
      dayjs(selectedDate).isSame(dayjs(date), 'day'),
    );

    if (isSelected) {
      const filteredDates = selectedDates.filter(selectedDate => {
        return !dayjs(selectedDate).isSame(dayjs(date), 'day');
      });

      setSelectedDates(filteredDates);
      setCheckedDate(filteredDates);
    } else {
      handleInitScheduleItem();
      setSelectedDates([...selectedDates, date]);
      setCheckedDate([...checkedDate, date]);
    }
  };

  const handleItemClick = (
    eventInfo: EventContentArg,
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    e.stopPropagation();

    setCheckedDate([]);

    handleIsOpenUpdateOptionSheet({
      isOpen: true,
      sheetType: OptionSheetType.UPDATE,
      scheduleItem: calendarList?.find(
        item => item.id === eventInfo.event.extendedProps.id,
      ),
    });
  };

  useEffect(() => {
    if (isClearCalendarData) {
      setSelectedDates([]);
    }
  }, [isClearCalendarData]);

  useEffect(() => {
    if (checkedDate) {
      const formatDate = checkedDate.map(item => new Date(item));

      setSelectedDates(formatDate);
    }
  }, [checkedDate]);

  const renderStatusItem = (eventInfo: EventContentArg): JSX.Element => {
    const item: ScheduleParam = eventInfo.event.extendedProps.item;

    switch (item.status) {
      case ScheduleState.EDITING: {
        return (
          <ScheduleCardBox
            className="schedule-card-box"
            $isSelected={
              selectedItem ===
              new Date(
                eventInfo.event.extendedProps.item.term.startedAt,
              ).getTime()
            }
            onClick={e => handleItemClick(eventInfo, e)}
          >
            <Row gap="6px">
              <StatusButton $background="#414B56" />
              <Text color="#46474C" size="13px">
                등록중
              </Text>
            </Row>
            <span>{eventInfo.event.title.split('/')[0]}~</span>
            <span>{eventInfo.event.title.split('/')[1]}</span>
          </ScheduleCardBox>
        );
      }
      case ScheduleState.OPENED: {
        return (
          <ScheduleCardBox
            className="schedule-card-box"
            $isSelected={
              selectedItem ===
              new Date(
                eventInfo.event.extendedProps.item.term.startedAt,
              ).getTime()
            }
            onClick={e => handleItemClick(eventInfo, e)}
          >
            <Row gap="6px">
              <StatusButton $background="#0071E9" />
              <Text color="#46474C" size="13px">
                판매중
              </Text>
            </Row>
            <span>{eventInfo.event.title.split('/')[0]}~</span>
            <span>{eventInfo.event.title.split('/')[1]}</span>
          </ScheduleCardBox>
        );
      }
      case ScheduleState.PAUSED: {
        return (
          <ScheduleCardBox
            className="schedule-card-box"
            $isSelected={
              selectedItem ===
              new Date(
                eventInfo.event.extendedProps.item.term.startedAt,
              ).getTime()
            }
            onClick={e => handleItemClick(eventInfo, e)}
          >
            <Row gap="6px">
              <StatusButton $background="#FF9200" />
              <Text color="#46474C" size="13px">
                일시 중지
              </Text>
            </Row>
            <span>{eventInfo.event.title.split('/')[0]}~</span>
            <span>{eventInfo.event.title.split('/')[1]}</span>
          </ScheduleCardBox>
        );
      }
      case ScheduleState.SOLD_OUT: {
        return (
          <ScheduleCardBox
            className="schedule-card-box"
            $isSelected={
              selectedItem ===
              new Date(
                eventInfo.event.extendedProps.item.term.startedAt,
              ).getTime()
            }
            onClick={e => handleItemClick(eventInfo, e)}
          >
            <Row gap="6px">
              <StatusButton $background="#C2C4C8" />
              <Text color="#46474C" size="13px">
                매진
              </Text>
            </Row>
            <span>{eventInfo.event.title.split('/')[0]}~</span>
            <span>{eventInfo.event.title.split('/')[1]}</span>
          </ScheduleCardBox>
        );
      }
      case ScheduleState.CANCELED: {
        return (
          <ScheduleCardBox
            className="schedule-card-box"
            $isSelected={
              selectedItem ===
              new Date(
                eventInfo.event.extendedProps.item.term.startedAt,
              ).getTime()
            }
            onClick={e => handleItemClick(eventInfo, e)}
          >
            <Row gap="6px">
              <StatusButton $background="#FF4242" />
              <Text color="#46474C" size="13px">
                취소
              </Text>
            </Row>
            <span>{eventInfo.event.title.split('/')[0]}~</span>
            <span>{eventInfo.event.title.split('/')[1]}</span>
          </ScheduleCardBox>
        );
      }
      case ScheduleState.CLOSED: {
        return (
          <ScheduleCardBox
            className="schedule-card-box"
            $isSelected={
              selectedItem ===
              new Date(
                eventInfo.event.extendedProps.item.term.startedAt,
              ).getTime()
            }
            onClick={e => handleItemClick(eventInfo, e)}
          >
            <Row gap="6px">
              <StatusButton $background="#70737C" />
              <Text color="#46474C" size="13px">
                신청마감
              </Text>
            </Row>
            <span>{eventInfo.event.title.split('/')[0]}~</span>
            <span>{eventInfo.event.title.split('/')[1]}</span>
          </ScheduleCardBox>
        );
      }
      default: {
        return (
          <ScheduleCardBox
            className="schedule-card-box"
            $isSelected={
              selectedItem ===
              new Date(
                eventInfo.event.extendedProps.item.term.startedAt,
              ).getTime()
            }
            onClick={e => handleItemClick(eventInfo, e)}
          >
            <span>{eventInfo.event.title.split('/')[0]}~</span>
            <span>{eventInfo.event.title.split('/')[1]}</span>
          </ScheduleCardBox>
        );
      }
    }
  };

  return (
    <CustomCalendarBase
      isMobile={isMobile}
      events={events}
      selectedDates={selectedDates}
      handleDateClick={handleDateClick}
      eventContent={eventInfo => {
        return renderStatusItem(eventInfo);
      }}
    />
  );
};

export default UpdateCalendar;
